import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, FileText, Trash2, Edit3, Loader2 } from 'lucide-react';
import { getCotizaciones, eliminarCotizacion } from '../api';

const CotizacionesList = () => {
  const [cotizaciones, setCotizaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const fetchCotizaciones = async () => {
    setLoading(true);
    try {
      const res = await getCotizaciones();
      setCotizaciones(res.data);
    } catch (err) {
      console.error("Error cargando cotizaciones:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCotizaciones();
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm('¿Estás seguro de que deseas eliminar esta cotización?')) {
      try {
        await eliminarCotizacion(id);
        setCotizaciones(cotizaciones.filter(c => c.id !== id));
      } catch (err) {
        console.error("Error eliminando cotización:", err);
        alert("No se pudo eliminar la cotización.");
      }
    }
  };

  const filteredCotizaciones = cotizaciones.filter(c => 
    c.empresa.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.num.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-8 w-full bg-gray-50 min-h-full">
      <div className="max-w-7xl mx-auto mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 uppercase tracking-wider">Historial de Cotizaciones</h1>
          <p className="text-sm text-gray-500 mt-1">Gestiona y revisa las propuestas comerciales enviadas.</p>
        </div>
        <button 
          onClick={() => navigate('/cotizaciones/nueva')}
          className="bg-[#00a99d] text-white px-6 py-3 rounded-lg flex items-center gap-2 font-bold text-sm hover:bg-opacity-90 transition shadow-md"
        >
          <Plus size={18} /> NUEVA COTIZACIÓN
        </button>
      </div>

      <div className="max-w-7xl mx-auto bg-white p-4 rounded-lg shadow-sm mb-6 flex gap-4 items-center border border-gray-200">
        <div className="flex-1 flex items-center border rounded-lg px-3 py-2 text-gray-400 focus-within:border-[#00a99d] transition">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Buscar por empresa o número de referencia..." 
            className="ml-2 outline-none w-full text-sm text-gray-700"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 size={48} className="text-[#00a99d] animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Cargando cotizaciones...</p>
          </div>
        ) : filteredCotizaciones.length === 0 ? (
          <div className="bg-white p-16 text-center text-gray-500 rounded-xl shadow-sm border border-dashed border-gray-300 flex flex-col items-center">
            <FileText size={64} className="text-gray-200 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">Sin cotizaciones</h3>
            <p className="text-gray-500 mt-2 max-w-xs mx-auto">
              {searchTerm ? "No se encontraron resultados para tu búsqueda." : "Aún no has creado ninguna cotización profesional."}
            </p>
            {!searchTerm && (
              <button 
                onClick={() => navigate('/cotizaciones/nueva')}
                className="mt-6 text-[#00a99d] font-bold hover:underline"
              >
                Crear mi primera cotización ahora
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCotizaciones.map((cot) => (
              <div 
                key={cot.id}
                onClick={() => navigate(`/cotizaciones/${cot.id}`)}
                className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition cursor-pointer overflow-hidden group"
              >
                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div className="p-2 bg-green-50 rounded-lg text-[#2C4F1A]">
                      <FileText size={24} />
                    </div>
                    <span className="text-xs font-bold text-[#B8922A] bg-amber-50 px-2 py-1 rounded">
                      {cot.num}
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg mb-1 truncate">{cot.empresa}</h3>
                  <p className="text-sm text-gray-500 mb-4 truncate">At: {cot.at}</p>
                  
                  <div className="flex items-center gap-2 mb-4">
                    <div className="h-1 flex-1 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-[#00a99d] w-full"></div>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                    <span className="text-xs text-gray-400">
                      {new Date(cot.createdAt).toLocaleDateString()}
                    </span>
                    <div className="flex gap-2">
                      <button 
                        onClick={(e) => { e.stopPropagation(); navigate(`/cotizaciones/${cot.id}`); }}
                        className="p-2 text-gray-400 hover:text-[#00a99d] transition"
                        title="Editar"
                      >
                        <Edit3 size={18} />
                      </button>
                      <button 
                        onClick={(e) => handleDelete(cot.id, e)}
                        className="p-2 text-gray-400 hover:text-red-500 transition"
                        title="Eliminar"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CotizacionesList;
